import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { useAppContext } from '../context/AppContext';
import EnergyContext from '../context/EnergyContext';
import { createContextAwareApiClient } from '../api/contextAwareApiClient';
import { energyService } from '../api/energyApi';
import { useAuth } from '../auth/AuthContext';
import { realtimeService } from '../services/realtimeService';
import {
  extractCanopyTelemetryFromEnergyLatest,
  matchesCanopyId,
  normalizeTelemetryRecord,
} from '../utils/telemetryRecordNormalizer';
import Datalens from '../components/Datalens';
import CanopyAnimationPanel from '../components/CanopyAnimationPanel';

const pickNewerTelemetryRecord = (first, second) => {
  if (!first) return second || null;
  if (!second) return first || null;
  const firstHasController = Boolean(first.data?.sabalController);
  const secondHasController = Boolean(second.data?.sabalController);
  if (firstHasController && !secondHasController) return first;
  if (secondHasController && !firstHasController) return second;
  const firstMs = Date.parse(first.timestamp || first.observedAt || '');
  const secondMs = Date.parse(second.timestamp || second.observedAt || '');
  if (Number.isFinite(firstMs) && Number.isFinite(secondMs)) {
    return secondMs >= firstMs ? second : first;
  }
  return Number.isFinite(secondMs) ? second : first;
};

/**
 * CanopyView component displays real-time telemetry data for a single selected canopy.
 * Uses WebSocket real-time updates with fallback to polling when WebSocket fails.
 */
function CanopyView() {
  const { 
    customerContext, 
    fleetContext, 
    canopyContext,
    getContextData,
    validateContext 
  } = useAppContext();
  const auth = useAuth();
  const energyContext = useContext(EnergyContext);
  const energyLatestRef = useRef(null);
  energyLatestRef.current = energyContext?.raw?.latest || null;
  
  // Use ref to store the service to prevent infinite loops
  const telemetryServiceRef = useRef();
  telemetryServiceRef.current = createContextAwareApiClient(getContextData, validateContext).telemetryService;
  
  const [telemetryData, setTelemetryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRealTime] = useState(true);

  // Initialize real-time service
  useEffect(() => {
    if (energyContext?.synchronization !== 'commit-journal' && !auth.isGuest) realtimeService.initialize(telemetryServiceRef.current);
  }, [auth.isGuest, energyContext?.synchronization]);

  // Fetch initial/latest telemetry data for the selected canopy
  const fetchTelemetryData = useCallback(async (canopyId, { background = false } = {}) => {
    if (energyContext?.synchronization === 'commit-journal') {
      setTelemetryData(extractCanopyTelemetryFromEnergyLatest(energyLatestRef.current, canopyId, { canopyId, canopyName: canopyContext?.canopyName }));
      setLoading(false); setError(null); return;
    }
    if (!canopyId) {
      setTelemetryData(null);
      return;
    }

    const fallbackIdentity = {
      customerId: customerContext?.customerId,
      fleetId: fleetContext?.fleetId,
      canopyId,
      canopyName: canopyContext?.canopyName,
    };

    const cachedFromEnergy = extractCanopyTelemetryFromEnergyLatest(
      energyLatestRef.current,
      canopyId,
      fallbackIdentity
    );

    if (!background) {
      if (cachedFromEnergy) {
        setTelemetryData(cachedFromEnergy);
      } else {
        setLoading(true);
      }
      setError(null);
    }
    
    try {
      const legacyPromise = auth.isGuest ? Promise.resolve(null) : Promise.resolve()
        .then(() => telemetryServiceRef.current.getLatestTelemetry(canopyId))
        .catch(() => null);
      const energyPromise = Promise.resolve()
        .then(() => (
          typeof energyService?.getLatest === 'function'
            ? energyService.getLatest({
              customerId: customerContext?.customerId,
              fleetId: fleetContext?.fleetId,
              messageType: 'canopy.telemetry',
            })
            : null
        ))
        .catch(() => null);

      const [legacyResponse, energyResponse] = await Promise.all([legacyPromise, energyPromise]);

      const legacyRecord = legacyResponse && legacyResponse.success !== false
        ? normalizeTelemetryRecord(legacyResponse, fallbackIdentity)
        : null;
      const energyRecord = energyResponse && energyResponse.success !== false
        ? extractCanopyTelemetryFromEnergyLatest(energyResponse, canopyId, fallbackIdentity)
        : null;

      const resolvedRecord = pickNewerTelemetryRecord(
        pickNewerTelemetryRecord(legacyRecord, energyRecord),
        cachedFromEnergy
      );

      if (resolvedRecord) {
        setTelemetryData(resolvedRecord);
        setError(null);
      } else if (!background) {
        const legacyNotFound = legacyResponse?.status === 404;
        const energySucceededEmpty = energyResponse?.success === true;
        if (legacyNotFound || energySucceededEmpty) {
          setTelemetryData(null);
        } else if (legacyResponse && legacyResponse.success === false && legacyResponse.message) {
          setError(`Error fetching telemetry data: ${legacyResponse.message}`);
        } else {
          setTelemetryData(null);
        }
      }
    } catch (err) {
      console.error('💥 SINGLE CANOPY TELEMETRY: Error fetching telemetry:', err);
      if (!background && !cachedFromEnergy) {
        setError(`Error fetching telemetry data: ${err.message}`);
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [customerContext?.customerId, fleetContext?.fleetId, canopyContext?.canopyName, auth.isGuest, energyContext?.synchronization]);

  // Fetch telemetry when canopy selection changes
  useEffect(() => {
    if (canopyContext?.canopyId) {
      fetchTelemetryData(canopyContext.canopyId);
    } else {
      setTelemetryData(null);
    }
  }, [canopyContext?.canopyId, fetchTelemetryData]);

  // Keep canopy telemetry updated when EnergyContext refreshes in the background
  useEffect(() => {
    if (!canopyContext?.canopyId || !energyContext?.raw?.latest) return;
    const latestFromEnergy = extractCanopyTelemetryFromEnergyLatest(
      energyContext.raw.latest,
      canopyContext.canopyId,
      {
        customerId: customerContext?.customerId,
        fleetId: fleetContext?.fleetId,
        canopyId: canopyContext.canopyId,
        canopyName: canopyContext.canopyName,
      }
    );
    if (latestFromEnergy) {
      setTelemetryData((prev) => pickNewerTelemetryRecord(prev, latestFromEnergy));
    }
  }, [
    canopyContext?.canopyId,
    canopyContext?.canopyName,
    customerContext?.customerId,
    fleetContext?.fleetId,
    energyContext?.raw?.latest,
  ]);

  // Real-time updates using WebSocket + 10-second polling for v2 canopy.telemetry
  useEffect(() => {
    if (!canopyContext?.canopyId || energyContext?.synchronization === 'commit-journal') return;

    const pollInterval = setInterval(() => {
      fetchTelemetryData(canopyContext.canopyId, { background: true });
    }, 10000);

    if (isRealTime && !auth.isGuest) {
      // Connect to WebSocket if not already connected
      realtimeService.connect();
      
      // Subscribe to telemetry updates for the selected canopy
      const handler = (data) => {
        if (matchesCanopyId(data, canopyContext.canopyId)) {
          const normalized = normalizeTelemetryRecord(data, {
            customerId: customerContext?.customerId,
            fleetId: fleetContext?.fleetId,
            canopyId: canopyContext.canopyId,
            canopyName: canopyContext.canopyName,
          });
          if (normalized) {
            setTelemetryData(normalized);
            setError(null);
          }
        }
      };

      // Subscribe to real-time updates
      realtimeService.subscribe('telemetry_update', handler);

      // Cleanup function
      return () => {
        clearInterval(pollInterval);
        realtimeService.unsubscribe('telemetry_update', handler);
      };
    }

    return () => {
      clearInterval(pollInterval);
    };
  }, [
    canopyContext?.canopyId,
    canopyContext?.canopyName,
    customerContext?.customerId,
    fleetContext?.fleetId,
    isRealTime,
    auth.isGuest,
    fetchTelemetryData,
    energyContext?.synchronization,
  ]);

  // Check if we have the required context
  if (!customerContext || !fleetContext) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a customer and fleet to view canopy telemetry data.
        </Alert>
      </Box>
    );
  }

  if (!canopyContext) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a canopy from the dropdown above to view its telemetry data.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading telemetry data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
      {/* Content: Only Datalens, no header or controls */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
        {!telemetryData ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No telemetry data available for this canopy.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Telemetry data will appear here when available.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: 'stretch',
              justifyContent: 'center',
              gap: 2,
              p: 2
            }}
          >
            {/* Left: Telemetry record */}
            <Box
              sx={{
                flex: { xs: 'none', lg: 3 },
                minWidth: 0,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Datalens record={telemetryData} modal={false} columns={4} />
            </Box>

            {/* Right: Canopy Animation for the same record */}
            <Box
              sx={{
                flex: { xs: 'none', lg: 2 },
                minWidth: 0,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 700 }}>
                <CanopyAnimationPanel record={telemetryData} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CanopyView; 